import Avatar, { Badge } from 'components/Avatar'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import Tooltip from 'components/Tooltip'
import JobViewModel from 'jobs/views/JobRoot/Job/JobViewModel'
import { observer } from 'mobx-react'
import React, { useEffect } from 'react'
import { timeago } from 'utils/date-util'
import * as Duration from 'dration'
import styles from './ConversationHeader.sass'
import { animated, useSpringValue } from 'react-spring'

interface ConversationHeaderProps {
  vm: JobViewModel
}

export default observer(function ConversationHeader({
  vm,
}: ConversationHeaderProps) {
  const timeAgoText = timeago(
    vm.participantMemberToDisplay?.lastSeen ?? new Date()
  )

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        {vm.voiceCallVM.callStep === 'IN_PROGRESS' ? (
          <VoiceCallParticipantInfo vm={vm} />
        ) : (
          <div className={styles.participant}>
            <div className={styles.avatarContainer}>
              <Avatar
                badge={
                  vm.championClient?.collaborator.member.user.online
                    ? Badge.Online
                    : Badge.Offline
                }
                size={38}
                subject={
                  vm.participantMemberToDisplay ??
                  vm.championClient?.collaborator.member
                }
              />
            </div>
            <div className={styles.memberInfo}>
              <div className={styles.displayName}>
                {vm.participantMemberToDisplay?.displayName ??
                  vm.championClient?.collaborator.member.displayName}
              </div>
              {vm.canViewLastSeenInfoInChat && (
                <div className={styles.lastSeen}>
                  {vm.participantMemberToDisplay
                    ? timeAgoText === 'now'
                      ? 'Active now'
                      : `Last seen ${timeAgoText} ago`
                    : 'Unclaimed'}
                </div>
              )}
            </div>
          </div>
        )}

        <div className={styles.actions}>
          {vm.conversationVM.canRefreshManually && (
            <Tooltip content="Refresh conversation" placement="bottom">
              <IconButton
                variant="ghost"
                onClick={() => vm.conversationVM.fetchLatestMessages()}
              >
                <Icon name="loader-arrows" />
              </IconButton>
            </Tooltip>
          )}
          {vm.job.canStartCall && (
            <Tooltip content="Start a video call" placement="bottom">
              <IconButton
                data-test="call-button"
                variant="ghost"
                onClick={vm.conversationVM.startCall}
                loading={vm.conversationVM.startCall.pending}
              >
                <Icon name="phone" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip
            content={
              vm.conversationVM.conversation.muted
                ? 'Unmute conversation'
                : 'Mute conversation'
            }
            placement="bottom"
          >
            <IconButton
              variant="ghost"
              onClick={() => vm.conversationVM.toggleMute()}
            >
              <Icon
                name={
                  vm.conversationVM.conversation.muted
                    ? 'notifications-thick-muted'
                    : 'notifications-thick'
                }
              />
            </IconButton>
          </Tooltip>
          {vm.job.canDownloadConversation && (
            <Tooltip content="Download conversation history" placement="bottom">
              <IconButton
                variant="ghost"
                onClick={vm.conversationVM.downloadConversation}
                data-test={`download-conversation-new`}
              >
                <Icon name="download" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  )
})

const VoiceCallParticipantInfo = observer(function VoiceCallParticipantInfo({
  vm,
}: ConversationHeaderProps) {
  // Pulse call recipient sound indicator outside react.
  // Twilio call volume goes from 0 to 1 indicating
  // volume intensity
  const volume = useSpringValue(0)
  const indicatorScale = volume.to([0, 1], [1, 2.5])

  useEffect(() => {
    function handleVolume(inputVolume: number, outputVolume: number) {
      volume.start(outputVolume)
    }

    vm.voiceCallVM.twilioCall?.on('volume', handleVolume)
    return () => {
      vm.voiceCallVM.twilioCall?.off('volume', handleVolume)
    }
  }, [vm.voiceCallVM.twilioCall, volume])

  return (
    <div className={styles.participant}>
      <div className={styles.iconContainer}>
        <animated.div
          className={styles.inputSoundIndicator}
          style={{
            scale: indicatorScale,
          }}
        ></animated.div>
        <div className={styles.icon}>
          <Icon name="phone" size={24} />
        </div>
      </div>
      <div className={styles.memberInfo}>
        <div className={styles.displayName}>
          {vm.participantMemberToDisplay?.displayName ??
            vm.championClient?.collaborator.member.displayName}
        </div>
        <div className={styles.lastSeen}>
          {Duration.format(vm.voiceCallVM.duration)}
        </div>
      </div>
    </div>
  )
})
